import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Check, X, MessageCircle, CalendarClock, Video, Phone, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SLOTS = Array.from({ length: 18 }, (_, i) => {
  const h = 10 + Math.floor(i / 2);
  return `${String(h).padStart(2, '0')}:${i % 2 ? '30' : '00'}`;
});

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rescheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  declined: 'bg-destructive/10 text-destructive border-destructive/20',
  cancelled: 'bg-secondary text-muted-foreground border-border',
  completed: 'bg-secondary text-muted-foreground border-border'
};

const pretty = (date, slot) =>
  `${new Date(`${date}T${slot}`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })} · ${slot} IST`;

const waLink = (appt, kind) => {
  const digits = (appt.phone || '').replace(/\D/g, '');
  const number = digits.length === 10 ? `91${digits}` : digits;
  const when = pretty(appt.date, appt.slot);
  const proposed = appt.proposed_slot ? pretty(appt.proposed_date, appt.proposed_slot) : '';
  const body = {
    approve: `Namaste ${appt.full_name}, your ${appt.call_type === 'video' ? 'video call' : 'call'} with Bandhani Heritage is confirmed for ${when}. We will call you on this number.`,
    propose: `Namaste ${appt.full_name}, thank you for your request for ${when}. Could we connect at ${proposed} instead?`,
    decline: `Namaste ${appt.full_name}, unfortunately we are not available at ${when}. Please pick another time on our website — we would love to connect.`,
    plain: `Namaste ${appt.full_name}, regarding your ${appt.call_type === 'video' ? 'video call' : 'call'} request for ${when} with Bandhani Heritage…`
  }[kind];
  return `https://wa.me/${number}?text=${encodeURIComponent(body)}`;
};

export const AppointmentsPanel = () => {
  const [appointments, setAppointments] = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [proposeFor, setProposeFor] = useState(null);
  const [proposeDate, setProposeDate] = useState('');
  const [proposeSlot, setProposeSlot] = useState('');
  const [proposeMsg, setProposeMsg] = useState('');
  const [testing, setTesting] = useState(false);

  const testNotification = async () => {
    setTesting(true);
    try {
      const res = await api.post('/appointments/admin/test-notification');
      toast.success(`Test alert ${res.data.message}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test message failed');
    } finally {
      setTesting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/appointments/admin/all', { params: { status } });
      setAppointments(res.data.appointments);
    } catch {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (appt, action, extra = {}) => {
    try {
      await api.put(`/appointments/admin/${appt.id}`, { action, ...extra });
      toast.success(action === 'approve' ? 'Appointment approved' : action === 'decline' ? 'Appointment declined' : action === 'complete' ? 'Marked completed' : 'New time suggested');
      load();
      if (action !== 'complete') window.open(waLink({ ...appt, ...extra, proposed_date: extra.proposed_date, proposed_slot: extra.proposed_slot }, action), '_blank', 'noopener');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await api.delete(`/appointments/admin/${id}`);
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const submitPropose = async () => {
    if (!proposeDate || !proposeSlot) { toast.error('Pick a date and time'); return; }
    await decide(proposeFor, 'propose', { proposed_date: proposeDate, proposed_slot: proposeSlot, admin_message: proposeMsg.trim() || null });
    setProposeFor(null); setProposeDate(''); setProposeSlot(''); setProposeMsg('');
  };

  return (
    <div data-testid="admin-appointments-panel">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-full w-48" data-testid="appointments-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {['all', 'pending', 'approved', 'rescheduled', 'confirmed', 'declined', 'cancelled', 'completed'].map((s) => (
              <SelectItem key={s} value={s}>{s === 'all' ? 'All bookings' : s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-full" onClick={load} data-testid="appointments-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
        </Button>
        <Button variant="ghost" className="rounded-full text-green-700" onClick={testNotification} disabled={testing} data-testid="test-whatsapp-btn">
          {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
          Send test WhatsApp
        </Button>
      </div>

      {appointments.length === 0 ? (
        <div className="border border-border rounded-xl p-12 text-center">
          <CalendarClock className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">No call bookings yet.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-appointments-list">
          {appointments.map((a) => (
            <div key={a.id} className="border border-border rounded-xl p-5" data-testid={`appointment-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-2">
                    {a.call_type === 'video' ? <Video className="h-4 w-4 text-primary" /> : <Phone className="h-4 w-4 text-primary" />}
                    {a.full_name}
                    <a href={`tel:${a.phone}`} className="text-sm text-muted-foreground hover:text-primary">{a.phone}</a>
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{pretty(a.date, a.slot)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {a.interest_item ? `Interested in: ${a.interest_item}` : `Budget: ${a.price_range}`}
                  </p>
                  {a.note && <p className="text-xs text-muted-foreground mt-1 italic">“{a.note}”</p>}
                  {a.status === 'rescheduled' && a.proposed_slot && (
                    <p className="text-xs text-blue-700 mt-2">You suggested: {pretty(a.proposed_date, a.proposed_slot)}</p>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${STATUS_STYLE[a.status] || 'bg-secondary border-border'}`} data-testid={`appointment-status-${a.id}`}>
                  {a.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {['pending', 'rescheduled'].includes(a.status) && (
                  <Button size="sm" className="rounded-full" onClick={() => decide(a, 'approve')} data-testid={`approve-${a.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                )}
                {['pending', 'approved', 'rescheduled'].includes(a.status) && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setProposeFor(a); setProposeDate(a.date); setProposeSlot(''); }} data-testid={`propose-${a.id}`}>
                    <CalendarClock className="h-3.5 w-3.5 mr-1" /> Suggest another time
                  </Button>
                )}
                <a href={waLink(a, 'plain')} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="rounded-full text-green-700 border-green-200 hover:bg-green-50" data-testid={`whatsapp-${a.id}`}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                </a>
                {['pending', 'approved', 'rescheduled'].includes(a.status) && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(a, 'decline')} data-testid={`decline-${a.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" /> Decline
                  </Button>
                )}
                {['approved', 'confirmed'].includes(a.status) && (
                  <Button size="sm" variant="ghost" className="rounded-full" onClick={() => decide(a, 'complete')} data-testid={`complete-${a.id}`}>
                    Mark done
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(a.id)} data-testid={`delete-appointment-${a.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!proposeFor} onOpenChange={(o) => !o && setProposeFor(null)}>
        <DialogContent className="max-w-md" data-testid="propose-dialog">
          <DialogHeader>
            <DialogTitle>Suggest another time</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Date</Label>
              <Input type="date" value={proposeDate} onChange={(e) => setProposeDate(e.target.value)} className="rounded-full mt-1" data-testid="propose-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Time (IST)</Label>
              <Select value={proposeSlot} onValueChange={setProposeSlot}>
                <SelectTrigger className="rounded-full mt-1" data-testid="propose-slot-select"><SelectValue placeholder="Select slot" /></SelectTrigger>
                <SelectContent>
                  {SLOTS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Message (optional)</Label>
              <Textarea value={proposeMsg} onChange={(e) => setProposeMsg(e.target.value)} rows={2} className="rounded-xl mt-1" data-testid="propose-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setProposeFor(null)}>Cancel</Button>
            <Button className="rounded-full" onClick={submitPropose} data-testid="propose-submit-btn">Send suggestion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPanel;
